/**
 * PreviewMetricsHeader — layer chip, status badges, full item name + ids.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { cn } from '@/lib/utils';

import type { PreviewRow } from '../../preview-row.types';
import { useFlowTagsFor } from '../../flow-tags-context';
import { fmtScore, layerLabel } from '../format';
import { CopyableId } from './state';

export interface PreviewMetricsHeaderProps {
  name: string;
  row: PreviewRow;
}

const PreviewMetricsHeader: FC<PreviewMetricsHeaderProps> = ({ name, row }) => {
  const { tags: flowTags, colorOf } = useFlowTagsFor(row.layer, row.itemId, row.pathId);
  return (
    <header className="space-y-1.5 border-b border-border/60 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className={cn(
            'inline-flex h-5 items-center rounded border border-border/60',
            'bg-muted/40 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-foreground',
          )}
        >
          {layerLabel(row.layer)}
        </span>
        {renderStatusBadges(row)}
        {typeof row.rawScore === 'number' ? (
          <span
            className="ml-auto inline-flex h-5 items-center gap-1 rounded border border-primary/30 bg-primary/10 px-1.5 text-[10px] font-semibold tabular-nums"
            title="Raw score — sum of this element's weighted group scores"
          >
            <span className="uppercase tracking-wide text-muted-foreground">
              Raw
            </span>
            <span className="text-foreground">{fmtScore(row.rawScore)}</span>
          </span>
        ) : null}
      </div>
      {flowTags.length > 0 ? (
        <div className="flex flex-wrap gap-1" title="Flow optimisation tags (raw-score ranking)">
          {flowTags.map((tag) => (
            <FlowTagChip key={tag} tag={tag} color={colorOf(tag)} />
          ))}
        </div>
      ) : null}
      <h3 className="text-xs font-semibold leading-snug text-foreground">
        {name || row.itemName || 'Untitled'}
      </h3>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px]">
        <CopyableId label="ID" value={row.itemId} />
        {row.pathId && row.layer !== 'paths' ? (
          <CopyableId label="Path" value={row.pathId} />
        ) : null}
      </div>
    </header>
  );
};

/** Distinct chip (⚡) so flow ranking tags aren't confused with performance tags. */
const FlowTagChip: FC<{ tag: string; color?: string }> = ({ tag, color }) => (
  <span
    className="inline-flex h-5 items-center gap-1 rounded-full border px-1.5 text-[10px] font-medium"
    style={
      color
        ? { backgroundColor: `${color}22`, color, borderColor: `${color}55` }
        : undefined
    }
  >
    <span aria-hidden>⚡</span>
    {tag}
  </span>
);

function renderStatusBadges(row: PreviewRow) {
  return (
    <div className="flex flex-wrap gap-1">
      <StatusBadge
        active
        tone={row.active ? 'success' : 'muted'}
        label={row.active ? 'Active' : 'Inactive'}
      />
      {row.excluded ? <StatusBadge active tone="danger" label="Excluded" /> : null}
      {row.passThrough ? (
        <StatusBadge active tone="info" label="Pass-through" />
      ) : null}
    </div>
  );
}

type Tone = 'success' | 'muted' | 'danger' | 'info';

const TONE_CLASSES: Record<Tone, string> = {
  success: 'border-emerald-400/40 bg-emerald-500/10 text-emerald-600',
  muted: 'border-border bg-muted/40 text-muted-foreground',
  danger: 'border-destructive/40 bg-destructive/10 text-destructive',
  info: 'border-sky-400/40 bg-sky-500/10 text-sky-600',
};

const StatusBadge: FC<{ active: boolean; tone: Tone; label: string }> = ({
  tone,
  label,
}) => (
  <span
    className={cn(
      'inline-flex h-5 items-center rounded-full border px-1.5 text-[10px] font-medium',
      TONE_CLASSES[tone],
    )}
  >
    {label}
  </span>
);

export default PreviewMetricsHeader;
